<template>
    <footer class="app-footer">
        <!-- Mobile Footer -->
        <div class="footer-mobile" v-if="!isDesktop">
            <div class="footer-links">
                <a href="/legal/terms-of-use" target="_blank">{{
                    $t('general.terms-of-use')
                }}</a>
                <span class="separator">|</span>
                <a href="/legal/privacy-policy" target="_blank">{{
                    $t('general.privacy-policy')
                }}</a>
                <!-- §2257 link hidden (remove v-if="false" to restore) -->
                <template v-if="false">
                    <span class="separator">|</span>
                    <a
                        href="/legal/custodian-records-and-age-verification"
                        target="_blank"
                        >{{ $t('general.section-2257') }}</a
                    >
                </template>
                <span class="separator">|</span>
                <a
                    href="#"
                    class="footer-link"
                    @click.prevent="isReportModalOpen = true"
                >
                    {{ $t('moderation.report_link') }}
                </a>
                <span class="separator">|</span>
                <a href="mailto:support@wispal.me">{{
                    $t('general.contact')
                }}</a>
            </div>

            <div class="footer-company">
                <div class="company-name">C4F Technologies OÜ (17202185)</div>
                <div class="company-address">
                    Tornimäe tn 5, Tallinn, Harju maakond, 10145, Estonia
                </div>
            </div>

            <div class="footer-divider"></div>

            <div class="footer-payment">
                <img
                    src="@/assets/images/footer/visa.svg"
                    alt="Visa"
                    class="payment-logo"
                />
                <img
                    src="@/assets/images/footer/mastercard.svg"
                    alt="Mastercard"
                    class="payment-logo"
                />
            </div>
        </div>

        <!-- Desktop Footer -->
        <div class="footer-desktop" v-else>
            <div class="footer-left">
                <div class="company-name">C4F Technologies OÜ (17202185)</div>
                <div class="company-address">
                    Tornimäe tn 5, Tallinn, Harju maakond, 10145, Estonia
                </div>
            </div>

            <div class="footer-center">
                <img
                    src="@/assets/images/footer/visa.svg"
                    alt="Visa"
                    class="payment-logo"
                />
                <img
                    src="@/assets/images/footer/mastercard.svg"
                    alt="Mastercard"
                    class="payment-logo"
                />
            </div>

            <div class="footer-right">
                <div class="footer-links">
                    <a href="/legal/terms-of-use" target="_blank">{{
                        $t('general.terms-of-use')
                    }}</a>
                    <span class="separator">|</span>
                    <a href="/legal/privacy-policy" target="_blank">{{
                        $t('general.privacy-policy')
                    }}</a>
                    <!-- §2257 link hidden (remove v-if="false" to restore) -->
                    <template v-if="false">
                        <span class="separator">|</span>
                        <a
                            href="/legal/custodian-records-and-age-verification"
                            target="_blank"
                            >{{ $t('general.section-2257') }}</a
                        >
                    </template>
                    <span class="separator">|</span>
                    <a
                        href="#"
                        class="footer-link"
                        @click.prevent="isReportModalOpen = true"
                    >
                        {{ $t('moderation.report_link') }}
                    </a>
                    <span class="separator">|</span>
                    <a href="mailto:support@wispal.me">{{
                        $t('general.contact')
                    }}</a>
                </div>
            </div>
        </div>
        <ReportModal
            :is-open="isReportModalOpen"
            @close="isReportModalOpen = false"
        />
    </footer>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import ReportModal from '../general-modals/ReportModal.vue'

const windowWidth = ref(window.innerWidth)
const isReportModalOpen = ref(false)

const updateWindowWidth = () => {
    windowWidth.value = window.innerWidth
}

onMounted(() => {
    window.addEventListener('resize', updateWindowWidth)
})

onUnmounted(() => {
    window.removeEventListener('resize', updateWindowWidth)
})

const isDesktop = computed(() => windowWidth.value > 768)
</script>

<style scoped>
.app-footer {
    background-color: #000;
    color: #fff;
    width: 100%;
}

/* Mobile Footer */
.footer-mobile {
    padding: 14px;
    padding-bottom: 36px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    text-align: center;
}

.footer-mobile .footer-links {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 4px;
}

.footer-mobile .footer-links a,
.footer-mobile .footer-links router-link {
    color: #fff;
    text-decoration: none;
    font-weight: 400;
    transition: opacity 0.3s;
    font-size: 11px;
    font-family: 'Nunito', sans-serif;
    line-height: 22px;
}

.footer-mobile .footer-links a:hover,
.footer-mobile .footer-links router-link:hover {
    opacity: 0.8;
}

.separator {
    color: #fff;
    font-weight: 300;
}

.footer-company {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.company-name {
    font-weight: 700;
    font-size: 12px;
    font-family: 'Nunito', sans-serif;
    line-height: 20px;
}

.company-address {
    font-size: 12px;
    font-weight: 400;
    font-family: 'Nunito', sans-serif;
    color: #fff;
    text-align: center;
    line-height: 20px;
}

.footer-divider {
    width: 80%;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.2);
}

.footer-payment {
    display: flex;
    align-items: center;
    gap: 16px;
}

/* Desktop Footer */
.footer-desktop {
    padding: 20px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
}

.footer-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    align-items: flex-start;
}

.footer-desktop .company-name {
    line-height: 16px;
}

.footer-desktop .company-address {
    line-height: 16px;
}

.footer-center {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;
    justify-content: center;
}

.footer-desktop .payment-logo {
    height: 20px;
    width: auto;
}

.footer-right {
    flex: 1;
    display: flex;
    justify-content: flex-end;
}

.footer-desktop .footer-links {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
}

.footer-desktop .footer-links a {
    color: #fff;
    text-decoration: none;
    font-weight: 400;
    transition: opacity 0.3s;
    white-space: nowrap;
}

.footer-desktop .footer-links a:hover {
    opacity: 0.8;
}

.footer-desktop .separator {
    color: #fff;
    font-weight: 300;
}
</style>
